/**
 * MemberAlertsStrip — at-a-glance member status for /dashboard.
 *
 * After Sachin 2026-02-28: ALWAYS surface (a) sessions remaining and
 * (b) when the subscription runs out, front and center on sign-in.
 * Includes a "View options" button that explains what happens at the
 * end of the subscription and lets the member self-serve resume / cancel /
 * reach out to a coach.
 *
 * Renders on /dashboard for parents/adult clients with profile_complete=true.
 * Hidden entirely if the member has no signal at all (no strikes, no sub,
 * no sessions, no renewing commitment) — keeps the dashboard clean for
 * fresh leads.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    AlertTriangle, AlertOctagon, Settings, Loader2, Calendar,
    Ticket, ChevronRight, Heart, MessageCircle, X, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import CancelMembershipModal from "./CancelMembershipModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export default function MemberAlertsStrip({ onChange }) {
    const [busy, setBusy] = useState(true);
    const [strikes, setStrikes] = useState({ late_cancels: 0, no_shows: 0 });
    const [renewal, setRenewal] = useState(null);
    const [profile, setProfile] = useState(null);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [optionsOpen, setOptionsOpen] = useState(false);

    const load = async () => {
        try {
            const [s, r, p] = await Promise.all([
                axios.get(`${API}/me/strikes`, { withCredentials: true }).catch(() => ({ data: { late_cancels: 0, no_shows: 0 } })),
                axios.get(`${API}/me/renewal-status`, { withCredentials: true }).catch(() => ({ data: null })),
                axios.get(`${API}/profile`, { withCredentials: true }).catch(() => ({ data: null })),
            ]);
            setStrikes(s.data || { late_cancels: 0, no_shows: 0 });
            setRenewal(r.data);
            setProfile(p.data);
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { load(); }, []);

    if (busy) {
        return (
            <div data-testid="member-alerts-loading" className="flex items-center gap-2 text-zinc-400 text-xs py-3">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading status…
            </div>
        );
    }

    const totalStrikes = (strikes.late_cancels || 0) + (strikes.no_shows || 0);
    const sub = profile?.active_subscription || renewal?.subscription || null;
    const commitment = renewal?.commitment || null;
    const sessionsRemaining = profile?.credits_total_remaining ?? null;
    const hasRenewableSub = !!(sub || commitment);
    const isScheduledToCancel = !!sub?.cancel_at_period_end;

    // Render nothing only if there's truly no signal — no sub, no commitment,
    // no sessions, no strikes. (Fresh leads with zero state.)
    if (totalStrikes === 0 && !hasRenewableSub && !sessionsRemaining) return null;

    const renewItem = sub || commitment;
    const renewDate = sub?.current_period_end || commitment?.commitment_ends_at;
    const daysToRenew = sub?.days_until_renewal ?? commitment?.days_until_renewal ?? null;

    return (
        <>
            <div data-testid="member-alerts-strip" className="mb-6 space-y-3">

                {/* HERO: Membership Status — always visible whenever the
                    member has an active sub OR any sessions. This is the
                    "front and center" card Sachin asked for 2026-02-28. */}
                {(hasRenewableSub || sessionsRemaining > 0) && (
                    <div
                        data-testid="membership-status-card"
                        className={`rounded-2xl border p-5 ${
                            isScheduledToCancel
                                ? "border-brand-red/60 bg-brand-red/10"
                                : "border-zinc-800 bg-gradient-to-br from-zinc-950 to-zinc-900"
                        }`}
                    >
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="flex-1 min-w-0">
                                <div className={`text-[10px] tracking-[0.25em] uppercase mb-1 ${
                                    isScheduledToCancel ? "text-brand-red" : "text-emerald-400"
                                }`}>
                                    {isScheduledToCancel ? "Membership ending soon" : "Membership · Active"}
                                </div>
                                <div className="font-display text-2xl tracking-wider text-white truncate">
                                    {renewItem?.plan_name || renewItem?.display_name || "Your membership"}
                                </div>
                            </div>
                        </div>

                        {/* Sessions + end-date pills */}
                        <div className="mt-4 grid grid-cols-2 gap-3">
                            <div data-testid="ms-sessions-pill" className="border border-zinc-800 bg-black/40 rounded-xl px-3 py-3">
                                <div className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-zinc-400">
                                    <Ticket className="w-3 h-3" /> Sessions left
                                </div>
                                <div className="mt-1 font-display text-3xl tracking-wider text-white">
                                    {sessionsRemaining ?? "—"}
                                </div>
                            </div>
                            <div data-testid="ms-renew-pill" className="border border-zinc-800 bg-black/40 rounded-xl px-3 py-3">
                                <div className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-zinc-400">
                                    <Calendar className="w-3 h-3" />
                                    {isScheduledToCancel ? "Ends" : "Renews"}
                                </div>
                                <div className="mt-1 font-display text-base sm:text-lg tracking-wider text-white truncate">
                                    {renewDate
                                        ? fmtDate(renewDate)
                                        : "—"}
                                </div>
                                {daysToRenew !== null && daysToRenew >= 0 && (
                                    <div className={`mt-0.5 text-[11px] ${
                                        isScheduledToCancel && daysToRenew <= 14 ? "text-brand-red" : "text-zinc-500"
                                    }`}>
                                        {daysToRenew === 0 ? "today" : daysToRenew === 1 ? "tomorrow" : `in ${daysToRenew} days`}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* What-happens-next CTA — always present when the
                            member has a sub/commitment so they can see their
                            options at any time, not only inside the 7-day window. */}
                        {hasRenewableSub && (
                            <button
                                type="button"
                                data-testid="ms-view-options-btn"
                                onClick={() => setOptionsOpen(true)}
                                className={`mt-4 w-full inline-flex items-center justify-between gap-2 px-4 py-3 rounded-xl border transition-colors ${
                                    isScheduledToCancel
                                        ? "bg-brand-red text-white border-brand-red hover:bg-brand-red/90"
                                        : "border-zinc-700 text-zinc-100 hover:border-brand-red hover:text-white"
                                }`}
                            >
                                <span className="text-sm font-semibold text-left">
                                    {isScheduledToCancel
                                        ? `View options — your membership ends ${fmtDate(renewDate)}`
                                        : "What happens when my membership ends?"}
                                </span>
                                <ChevronRight className="w-4 h-4 shrink-0" />
                            </button>
                        )}
                    </div>
                )}

                {/* Soft renewal banner — only when within the renewal window
                    AND NOT already cancelling (the hero card already screams
                    about that case). Kept as a secondary nudge. */}
                {renewal?.show_alert && renewItem && !isScheduledToCancel && (
                    <div
                        data-testid="renewal-alert-banner"
                        className="border border-amber-600/50 bg-amber-500/10 rounded-2xl px-5 py-3 flex flex-wrap items-center gap-3"
                    >
                        <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
                        <div className="text-sm flex-1 min-w-0 text-amber-100">
                            Renewal coming up — {daysToRenew === 0 ? "today" : daysToRenew === 1 ? "tomorrow" : `in ${daysToRenew} days`}.
                            Cancel at least 48 hours before to skip the next charge.
                        </div>
                    </div>
                )}

                {/* Strikes tile — still useful but secondary. */}
                {totalStrikes > 0 && (
                    <div
                        data-testid="strikes-tile"
                        className="border rounded-2xl px-4 py-3 border-brand-red/40 bg-brand-red/10"
                    >
                        <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-zinc-400">
                            <AlertOctagon className="w-3 h-3 text-brand-red" />
                            <span>This month&apos;s strikes</span>
                        </div>
                        <div className="mt-1 flex items-baseline gap-3">
                            <span data-testid="strikes-late-cancels" className="font-display text-2xl tracking-wider text-white">
                                {strikes.late_cancels}
                            </span>
                            <span className="text-[11px] text-zinc-400">late cancel{strikes.late_cancels === 1 ? "" : "s"}</span>
                            <span data-testid="strikes-no-shows" className="font-display text-2xl tracking-wider text-white ml-2">
                                {strikes.no_shows}
                            </span>
                            <span className="text-[11px] text-zinc-400">no-show{strikes.no_shows === 1 ? "" : "s"}</span>
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-1">
                            2 late cancels or 2 no-shows in a cycle = 1 session forfeited.
                        </div>
                    </div>
                )}
            </div>

            <CancelMembershipModal
                open={cancelOpen}
                onClose={() => setCancelOpen(false)}
                renewItem={renewItem}
                onCancelled={async () => {
                    await load();
                    onChange?.();
                }}
            />

            {optionsOpen && (
                <MembershipOptionsModal
                    renewItem={renewItem}
                    isScheduledToCancel={isScheduledToCancel}
                    renewDate={renewDate}
                    sessionsRemaining={sessionsRemaining}
                    onClose={() => setOptionsOpen(false)}
                    onOpenCancel={() => { setOptionsOpen(false); setCancelOpen(true); }}
                    onResumed={async () => { setOptionsOpen(false); await load(); onChange?.(); }}
                />
            )}
        </>
    );
}


/**
 * MembershipOptionsModal — what happens when the membership ends + the
 * choices the parent has. Two flavors:
 *   - sub is currently RENEWING:  do-nothing copy + "talk to coach" + "cancel anyway"
 *   - sub is currently CANCELLING: "resume" CTA up top + "talk to coach" + "keep cancelling"
 */
function MembershipOptionsModal({
    renewItem, isScheduledToCancel, renewDate, sessionsRemaining,
    onClose, onOpenCancel, onResumed,
}) {
    const [resuming, setResuming] = useState(false);

    const handleResume = async () => {
        setResuming(true);
        try {
            const r = await axios.post(`${API}/me/resume-subscription`, {}, { withCredentials: true });
            if (r.data?.resumed > 0) {
                toast.success("Subscription resumed", {
                    description: "Your membership will keep renewing — no more action needed.",
                });
                onResumed?.();
            } else {
                toast.info("Nothing to resume on your account.");
                onResumed?.();
            }
        } catch (e) {
            toast.error("Couldn't resume", {
                description: e?.response?.data?.detail || "Try the Manage billing button on /dashboard/billing.",
            });
        } finally {
            setResuming(false);
        }
    };

    return (
        <div data-testid="ms-options-modal" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-950 z-10">
                    <h3 className="text-lg font-semibold">
                        {isScheduledToCancel ? "Membership ending soon" : "What happens when my membership ends?"}
                    </h3>
                    <button type="button" data-testid="ms-options-close" onClick={onClose} className="text-zinc-200 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {/* Quick recap line */}
                    <p className="text-sm text-zinc-300 leading-relaxed">
                        <span className="font-semibold text-white">
                            {renewItem?.plan_name || renewItem?.display_name || "Your membership"}
                        </span>
                        {isScheduledToCancel ? " is scheduled to end on " : " renews on "}
                        <span className="font-semibold text-white">{fmtDate(renewDate)}</span>.
                        {sessionsRemaining > 0 && (
                            <>
                                {" "}You currently have{" "}
                                <span className="font-semibold text-white">{sessionsRemaining}</span> session
                                {sessionsRemaining === 1 ? "" : "s"} on your account.
                            </>
                        )}
                    </p>

                    {/* Resume hero — only when scheduled to cancel */}
                    {isScheduledToCancel && (
                        <button
                            type="button"
                            data-testid="ms-option-resume"
                            onClick={handleResume}
                            disabled={resuming}
                            className="w-full text-left bg-brand-red text-white rounded-xl p-4 hover:bg-brand-red/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
                        >
                            <RefreshCw className={`w-5 h-5 shrink-0 ${resuming ? "animate-spin" : ""}`} />
                            <div className="flex-1">
                                <div className="font-semibold text-sm">Keep my membership — resume auto-renew</div>
                                <div className="text-xs text-white/90 mt-0.5">
                                    Clears the scheduled cancellation. You&apos;ll keep training without interruption.
                                </div>
                            </div>
                        </button>
                    )}

                    {/* What happens explainer */}
                    <div className="border border-zinc-800 bg-zinc-900/50 rounded-xl p-4">
                        <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-400 mb-2">
                            {isScheduledToCancel ? "If you do nothing" : "If you ever cancel"}
                        </div>
                        <ul className="text-sm text-zinc-200 space-y-2 leading-relaxed">
                            {isScheduledToCancel ? (
                                <>
                                    <li>• Your membership ends on {fmtDate(renewDate)}.</li>
                                    <li>• You won&apos;t be charged again.</li>
                                    {sessionsRemaining > 0 && (
                                        <li>• Unused sessions convert at a 2:1 ratio — you&apos;d keep about <span className="font-semibold text-white">{Math.ceil(sessionsRemaining / 2)}</span> of your {sessionsRemaining}.</li>
                                    )}
                                    <li>• Workout access locks once the period ends.</li>
                                </>
                            ) : (
                                <>
                                    <li>• Membership auto-renews on {fmtDate(renewDate)} — no action needed.</li>
                                    <li>• You can cancel at any time, but to skip the next charge you must cancel <span className="font-semibold text-white">at least 48 hours before</span> the renewal date.</li>
                                    <li>• Unused sessions convert at a 2:1 ratio if you cancel.</li>
                                </>
                            )}
                        </ul>
                    </div>

                    {/* Talk to coach */}
                    <Link
                        to="/dashboard/messages?topic=Membership%20question"
                        data-testid="ms-option-talk-to-coach"
                        onClick={onClose}
                        className="block border border-zinc-800 bg-zinc-900/50 rounded-xl p-4 hover:border-brand-red hover:bg-zinc-900 transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <MessageCircle className="w-5 h-5 text-brand-red shrink-0" />
                            <div className="flex-1">
                                <div className="font-semibold text-sm text-white">Talk to your coach first</div>
                                <div className="text-xs text-zinc-400 mt-0.5">
                                    Questions about pausing, switching tiers, or anything else.
                                </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-zinc-500" />
                        </div>
                    </Link>

                    {/* Manage billing in Stripe portal */}
                    <Link
                        to="/dashboard/billing"
                        data-testid="ms-option-manage-billing"
                        onClick={onClose}
                        className="block border border-zinc-800 bg-zinc-900/50 rounded-xl p-4 hover:border-zinc-600 transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <Settings className="w-5 h-5 text-zinc-300 shrink-0" />
                            <div className="flex-1">
                                <div className="font-semibold text-sm text-white">Manage billing</div>
                                <div className="text-xs text-zinc-400 mt-0.5">
                                    Update card, view invoices, see plan details.
                                </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-zinc-500" />
                        </div>
                    </Link>

                    {/* Cancel-now CTA — only show when NOT already cancelling */}
                    {!isScheduledToCancel && (
                        <button
                            type="button"
                            data-testid="ms-option-cancel"
                            onClick={onOpenCancel}
                            className="w-full text-left border border-zinc-800 bg-zinc-900/50 rounded-xl p-4 hover:border-brand-red transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <Heart className="w-5 h-5 text-zinc-500 shrink-0" />
                                <div className="flex-1">
                                    <div className="font-semibold text-sm text-zinc-200">Cancel my membership</div>
                                    <div className="text-xs text-zinc-500 mt-0.5">
                                        Self-serve cancel. Sessions convert at 2:1.
                                    </div>
                                </div>
                                <ChevronRight className="w-4 h-4 text-zinc-500" />
                            </div>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
